<template>
  <div class="account-access-page">
    <header class="page-header">
      <div>
        <h2>Account Access</h2>
        <p>Find a user by email, review their access status, change their email, and suspend, ban, or restore the account.</p>
      </div>
    </header>

    <section class="search-panel" aria-labelledby="account-search-title">
      <div class="section-heading">
        <span class="section-step">1</span>
        <div>
          <h3 id="account-search-title">Find an account</h3>
          <p>Enter the complete email address associated with the user.</p>
        </div>
      </div>

      <form class="search-form" @submit.prevent="searchAccount">
        <el-input
          v-model.trim="searchEmail"
          :disabled="emailUpdateLoading || updateLoading"
          type="email"
          autocomplete="off"
          clearable
          prefix-icon="el-icon-message"
          placeholder="user@example.com"
          aria-label="User email address"
          @input="handleSearchInput"
          @clear="handleSearchInput"
        />
        <el-button
          native-type="submit"
          type="primary"
          icon="el-icon-search"
          :loading="searchLoading"
          :disabled="!searchEmail || emailUpdateLoading || updateLoading"
        >
          Search account
        </el-button>
      </form>

      <el-alert
        v-if="searchError"
        :title="searchError"
        type="error"
        show-icon
        :closable="false"
        class="search-alert"
      />
    </section>

    <template v-if="account">
      <section class="account-panel" aria-labelledby="account-summary-title">
        <div class="account-identity">
          <span class="account-avatar" aria-hidden="true">{{ accountInitial }}</span>
          <div>
            <div class="identity-label">Account</div>
            <h3 id="account-summary-title">{{ account.user_email }}</h3>
            <span class="user-id">{{ account.user_id }}</span>
          </div>
          <div class="status-summary">
            <span>Current status</span>
            <el-tag :type="statusTagType(account.account_status)" effect="dark">
              {{ statusLabel(account.account_status) }}
            </el-tag>
          </div>
        </div>

        <el-alert
          v-if="account.effective_status && account.effective_status !== account.account_status"
          :title="`Stored as ${statusLabel(account.account_status)}, but access is currently ${statusLabel(account.effective_status).toLowerCase()}.`"
          description="A temporary suspension has expired. Set the account to Active to make the stored status match its effective access."
          type="warning"
          show-icon
          :closable="false"
          class="effective-alert"
        />

        <dl class="account-details">
          <div>
            <dt>Effective access</dt>
            <dd>
              <el-tag size="small" :type="statusTagType(account.effective_status)">
                {{ statusLabel(account.effective_status) }}
              </el-tag>
            </dd>
          </div>
          <div>
            <dt>Suspension ends</dt>
            <dd>{{ formatDateTime(account.suspended_until, 'No scheduled end') }}</dd>
          </div>
          <div>
            <dt>Last status update</dt>
            <dd>{{ formatDateTime(account.updated_at, 'Not recorded') }}</dd>
          </div>
          <div class="wide-detail">
            <dt>Admin-only reason</dt>
            <dd>{{ account.reason || 'No reason recorded' }}</dd>
          </div>
          <div class="wide-detail">
            <dt>User-facing message</dt>
            <dd>{{ account.public_message || 'Default app message' }}</dd>
          </div>
        </dl>
      </section>

      <section class="update-panel" aria-labelledby="email-update-title">
        <div class="section-heading update-heading">
          <span class="section-step"><i class="el-icon-message" /></span>
          <div>
            <h3 id="email-update-title">Change account email</h3>
            <p>The new email will be used for sign-in. Confirm with your admin password.</p>
          </div>
        </div>
        <form class="search-form" @submit.prevent="openEmailConfirmation">
          <el-input
            v-model.trim="newEmail"
            type="email"
            maxlength="254"
            autocomplete="off"
            :disabled="emailUpdateLoading"
            placeholder="New email address"
            aria-label="New account email address"
          />
          <el-button native-type="submit" type="primary" :disabled="!canChangeEmail">
            Change email
          </el-button>
        </form>
        <p v-if="newEmail && !hasValidNewEmail" class="field-error">Enter a valid, complete email address.</p>
      </section>

      <section class="update-panel" aria-labelledby="status-update-title">
        <div class="section-heading update-heading">
          <span class="section-step">2</span>
          <div>
            <h3 id="status-update-title">Change account status</h3>
            <p>Select the access level and record why the change is being made.</p>
          </div>
        </div>

        <div class="status-options" role="radiogroup" aria-label="New account status">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="status-option"
            :class="[`status-${option.value}`, { selected: form.account_status === option.value }]"
            role="radio"
            :aria-checked="form.account_status === option.value ? 'true' : 'false'"
            @click="selectStatus(option.value)"
          >
            <span class="status-icon"><i :class="option.icon" /></span>
            <span class="status-copy">
              <strong>{{ option.label }}</strong>
              <small>{{ option.description }}</small>
            </span>
            <i v-if="form.account_status === option.value" class="el-icon-check selected-check" />
          </button>
        </div>

        <el-alert
          v-if="form.account_status !== 'active'"
          title="This change takes effect immediately"
          description="The user will be signed out on active devices and blocked from email code, password, mobile, device, and two-factor sign-in."
          :type="form.account_status === 'banned' ? 'error' : 'warning'"
          show-icon
          :closable="false"
          class="impact-alert"
        />

        <el-form label-position="top" class="status-form" @submit.native.prevent>
          <div v-if="form.account_status === 'suspended'" class="form-block">
            <div class="field-label">Suspension duration</div>
            <el-radio-group v-model="suspensionMode" size="small">
              <el-radio-button label="indefinite">Until manually restored</el-radio-button>
              <el-radio-button label="until">Until a specific time</el-radio-button>
            </el-radio-group>
            <el-date-picker
              v-if="suspensionMode === 'until'"
              v-model="form.suspended_until"
              type="datetime"
              placeholder="Select suspension end"
              :picker-options="futurePickerOptions"
              class="suspension-picker"
            />
            <p v-if="suspensionMode === 'until' && form.suspended_until && !hasValidSuspensionEnd" class="field-error">
              Suspension end must be in the future.
            </p>
          </div>

          <el-form-item :required="form.account_status !== 'active'">
            <template slot="label">
              <span>Internal reason</span>
              <el-tooltip content="Stored in the moderation audit record and never shown to the user." placement="top">
                <i class="el-icon-info label-info" />
              </el-tooltip>
            </template>
            <el-input
              v-model="form.reason"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              :placeholder="form.account_status === 'active' ? 'Optional note explaining why access is being restored' : 'Required: describe the policy, abuse, or payment issue'"
            />
          </el-form-item>

          <el-form-item v-if="form.account_status !== 'active'">
            <template slot="label">
              <span>User-facing message</span>
              <span class="optional-label">Optional</span>
            </template>
            <el-input
              v-model="form.public_message"
              type="textarea"
              :rows="3"
              maxlength="500"
              show-word-limit
              placeholder="Safe message shown in the web and mobile login screens"
            />
            <p class="field-help">Do not include internal evidence, fraud details, or private review notes.</p>
          </el-form-item>
        </el-form>

        <div class="update-actions">
          <span v-if="!hasChanges" class="no-change">Choose a different status or edit the current status details.</span>
          <el-button @click="resetFormFromAccount">Reset</el-button>
          <el-button
            type="primary"
            :loading="updateLoading"
            :disabled="!canUpdate"
            @click="confirmUpdate"
          >
            {{ updateButtonLabel }}
          </el-button>
        </div>
      </section>
    </template>

    <section v-else-if="!searchLoading && !searchError" class="empty-panel" aria-live="polite">
      <span class="empty-icon"><i class="el-icon-user" /></span>
      <h3>Search for an account to begin</h3>
      <p>The account status and moderation controls will appear here.</p>
    </section>
    <el-dialog
      title="Confirm email change"
      :visible.sync="emailDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      :close-on-press-escape="!emailUpdateLoading"
      :show-close="!emailUpdateLoading"
      :before-close="closeEmailConfirmation"
      @closed="clearEmailConfirmation"
    >
      <p>Change <strong>{{ emailChangeTarget.currentEmail }}</strong> to <strong>{{ emailChangeTarget.newEmail }}</strong>?</p>
      <form @submit.prevent="updateEmail">
        <label for="admin-email-password">Admin password</label>
        <el-input
          id="admin-email-password"
          v-model="adminPassword"
          type="password"
          autocomplete="current-password"
          :disabled="emailUpdateLoading"
          placeholder="Enter your admin password"
          class="password-confirmation"
        />
        <el-alert v-if="emailUpdateError" :title="emailUpdateError" type="error" show-icon :closable="false" />
        <div class="email-dialog-actions">
          <el-button :disabled="emailUpdateLoading" @click="closeEmailConfirmation()">Cancel</el-button>
          <el-button native-type="submit" type="primary" :loading="emailUpdateLoading" :disabled="!adminPassword || emailUpdateLoading">
            Confirm email change
          </el-button>
        </div>
      </form>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getAccountStatusForAdmin,
  updateAccountStatusForAdmin,
  updateAccountEmailForAdmin
} from '@/apis/lfAdmin'

const DEFAULT_PUBLIC_MESSAGES = {
  suspended: 'This account is temporarily suspended. Please contact support for assistance.',
  banned: 'This account has been disabled. Please contact support if you believe this is an error.'
}

export default {
  name: 'LFAdminAccountAccess',
  data() {
    return {
      searchEmail: '',
      searchedEmail: '',
      searchLoading: false,
      searchError: '',
      updateLoading: false,
      newEmail: '',
      adminPassword: '',
      emailDialogVisible: false,
      emailUpdateLoading: false,
      emailUpdateError: '',
      emailChangeTarget: {},
      account: null,
      suspensionMode: 'indefinite',
      form: {
        account_status: 'active',
        reason: '',
        public_message: '',
        suspended_until: null
      },
      statusOptions: [
        {
          value: 'active',
          label: 'Active',
          description: 'Restore normal account access.',
          icon: 'el-icon-circle-check'
        },
        {
          value: 'suspended',
          label: 'Suspended',
          description: 'Block access temporarily or indefinitely.',
          icon: 'el-icon-video-pause'
        },
        {
          value: 'banned',
          label: 'Banned',
          description: 'Disable access until an admin restores it.',
          icon: 'el-icon-circle-close'
        }
      ],
      futurePickerOptions: {
        disabledDate(date) {
          return date.getTime() < dayjs().startOf('day').valueOf()
        }
      }
    }
  },
  computed: {
    accountInitial() {
      return String((this.account && this.account.user_email) || '?').charAt(0).toUpperCase()
    },
    normalizedEmail() {
      return String(this.searchEmail || '').trim().toLowerCase()
    },
    hasValidEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.normalizedEmail)
    },
    normalizedNewEmail() {
      return String(this.newEmail || '').trim().toLowerCase()
    },
    hasValidNewEmail() {
      return this.normalizedNewEmail.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.normalizedNewEmail)
    },
    canChangeEmail() {
      return !!this.account && this.account.account_status !== 'deleted' && this.hasValidNewEmail &&
        this.normalizedNewEmail !== String(this.account.user_email || '').trim().toLowerCase() &&
        !this.emailUpdateLoading && !this.updateLoading && !this.searchLoading
    },
    hasValidSuspensionEnd() {
      if (this.form.account_status !== 'suspended' || this.suspensionMode !== 'until') return true
      return !!this.form.suspended_until && dayjs(this.form.suspended_until).unix() > dayjs().unix()
    },
    hasRequiredReason() {
      return this.form.account_status === 'active' || !!String(this.form.reason || '').trim()
    },
    hasChanges() {
      if (!this.account) return false
      if (this.form.account_status !== this.account.account_status) return true

      if (this.form.account_status === 'active') return false
      if (String(this.form.reason || '').trim() !== String(this.account.reason || '').trim()) return true
      if (String(this.form.public_message || '').trim() !== String(this.account.public_message || '').trim()) return true

      if (this.form.account_status === 'suspended') {
        const currentUntil = Number(this.account.suspended_until || 0)
        const nextUntil = this.suspensionMode === 'until' && this.form.suspended_until
          ? dayjs(this.form.suspended_until).unix()
          : 0
        return currentUntil !== nextUntil
      }
      return false
    },
    canUpdate() {
      return this.hasChanges && this.hasRequiredReason && this.hasValidSuspensionEnd && !this.updateLoading && !this.emailUpdateLoading
    },
    updateButtonLabel() {
      if (this.form.account_status === 'active') return 'Restore account'
      if (this.form.account_status === 'banned') return 'Ban account'
      return 'Suspend account'
    }
  },
  methods: {
    openEmailConfirmation() {
      if (!this.canChangeEmail) return
      this.emailChangeTarget = {
        userId: this.account.user_id,
        currentEmail: this.account.user_email,
        newEmail: this.normalizedNewEmail
      }
      this.adminPassword = ''
      this.emailUpdateError = ''
      this.emailDialogVisible = true
    },
    closeEmailConfirmation(done) {
      if (this.emailUpdateLoading) return
      this.adminPassword = ''
      this.emailDialogVisible = false
      if (typeof done === 'function') done()
    },
    clearEmailConfirmation() {
      this.adminPassword = ''
      this.emailUpdateError = ''
      this.emailChangeTarget = {}
    },
    async updateEmail() {
      if (!this.adminPassword || this.emailUpdateLoading || !this.emailChangeTarget.userId) return
      this.emailUpdateLoading = true
      this.emailUpdateError = ''
      try {
        const response = await updateAccountEmailForAdmin({
          user_id: this.emailChangeTarget.userId,
          expected_email: this.emailChangeTarget.currentEmail,
          new_email: this.emailChangeTarget.newEmail,
          admin_password: this.adminPassword
        })
        const result = response && response.data
        if (!result || Number(result.code) !== 0 || !result.data) {
          throw new Error(this.apiErrorMessage(result && result.msg, 'Unable to change this email.'))
        }
        this.account = result.data
        this.searchEmail = result.data.user_email
        this.searchedEmail = String(result.data.user_email).trim().toLowerCase()
        this.newEmail = ''
        this.emailDialogVisible = false
        this.$message.success('Account email updated.')
      } catch (error) {
        this.emailUpdateError = error.message || 'Unable to change this email.'
      } finally {
        this.adminPassword = ''
        this.emailUpdateLoading = false
      }
    },
    handleSearchInput() {
      this.searchError = ''
      if (this.account && this.normalizedEmail !== this.searchedEmail) {
        this.account = null
      }
    },
    async searchAccount() {
      if (this.searchLoading || this.emailUpdateLoading || this.updateLoading) return
      this.newEmail = ''
      if (!this.hasValidEmail) {
        this.searchError = 'Enter a valid, complete email address.'
        return
      }

      this.searchLoading = true
      this.searchError = ''
      this.account = null
      try {
        const response = await getAccountStatusForAdmin({ user_email: this.normalizedEmail })
        const result = response && response.data
        if (!result || Number(result.code) !== 0 || !result.data) {
          if (result && Number(result.code) === -404) {
            throw new Error('No account was found for this email address.')
          }
          throw new Error(this.apiErrorMessage(result && result.msg, 'Unable to load this account.'))
        }
        this.account = result.data
        this.searchedEmail = this.normalizedEmail
        this.resetFormFromAccount()
      } catch (error) {
        this.searchError = error.message || 'Unable to load this account.'
      } finally {
        this.searchLoading = false
      }
    },
    selectStatus(status) {
      const previousStatus = this.form.account_status
      const previousDefault = DEFAULT_PUBLIC_MESSAGES[previousStatus] || ''
      this.form.account_status = status

      if (status === 'active') {
        this.form.public_message = ''
        this.form.suspended_until = null
        this.suspensionMode = 'indefinite'
        return
      }

      if (!this.form.public_message || this.form.public_message === previousDefault) {
        this.form.public_message = DEFAULT_PUBLIC_MESSAGES[status]
      }
      if (status !== 'suspended') {
        this.form.suspended_until = null
        this.suspensionMode = 'indefinite'
      }
    },
    resetFormFromAccount() {
      if (!this.account) return
      const until = Number(this.account.suspended_until || 0)
      this.form = {
        account_status: this.account.account_status || 'active',
        reason: this.account.reason || '',
        public_message: this.account.public_message || '',
        suspended_until: until > 0 ? new Date(until * 1000) : null
      }
      this.suspensionMode = until > 0 ? 'until' : 'indefinite'
    },
    async confirmUpdate() {
      if (!this.canUpdate) {
        if (!this.hasRequiredReason) this.$message.warning('Enter an internal reason for this restriction.')
        else if (!this.hasValidSuspensionEnd) this.$message.warning('Choose a future suspension end time.')
        return
      }

      const status = this.form.account_status
      const statusText = this.statusLabel(status).toLowerCase()
      const actionDescription = status === 'active'
        ? `Restore access for ${this.account.user_email}? The user will need to sign in again.`
        : `Set ${this.account.user_email} to ${statusText}? Active sessions will be revoked immediately.`

      try {
        await this.$confirm(actionDescription, `Confirm ${statusText} status`, {
          confirmButtonText: status === 'active' ? 'Restore access' : `Confirm ${statusText}`,
          cancelButtonText: 'Cancel',
          type: status === 'banned' ? 'error' : 'warning',
          closeOnClickModal: false
        })
      } catch (error) {
        return
      }

      this.updateAccount()
    },
    async updateAccount() {
      this.updateLoading = true
      try {
        const payload = {
          user_id: this.account.user_id,
          account_status: this.form.account_status,
          reason: String(this.form.reason || '').trim()
        }
        if (this.form.account_status !== 'active') {
          payload.public_message = String(this.form.public_message || '').trim()
        }
        if (this.form.account_status === 'suspended' && this.suspensionMode === 'until') {
          payload.suspended_until = dayjs(this.form.suspended_until).unix()
        }

        const response = await updateAccountStatusForAdmin(payload)
        const result = response && response.data
        if (!result || Number(result.code) !== 0 || !result.data) {
          throw new Error(this.apiErrorMessage(result && result.msg, 'Unable to update this account.'))
        }

        this.account = result.data
        this.resetFormFromAccount()
        this.$message.success(`Account status updated to ${this.statusLabel(this.account.account_status)}.`)
      } catch (error) {
        this.$message.error(error.message || 'Unable to update this account.')
      } finally {
        this.updateLoading = false
      }
    },
    statusLabel(status) {
      const labels = {
        active: 'Active',
        suspended: 'Suspended',
        banned: 'Banned',
        deleted: 'Deleted'
      }
      return labels[String(status || '').toLowerCase()] || 'Unknown'
    },
    statusTagType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'active') return 'success'
      if (value === 'suspended') return 'warning'
      if (value === 'banned' || value === 'deleted') return 'danger'
      return 'info'
    },
    formatDateTime(timestamp, fallback) {
      const value = Number(timestamp || 0)
      return value > 0 ? dayjs(value * 1000).format('YYYY-MM-DD HH:mm:ss') : fallback
    },
    apiErrorMessage(code, fallback) {
      const messages = {
        admin_password_invalid: 'The admin password is incorrect or has not been configured.',
        invalid_email: 'Enter a valid, complete email address.',
        email_already_in_use: 'This email address is already used by another account.',
        email_unchanged: 'Enter a different email address.',
        account_email_conflict: 'The account email changed. Close this dialog and search for the account again.',
        deleted_account_email_cannot_be_changed: 'A deleted account cannot have its email changed.',
        account_email_update_failed: 'The email could not be saved. Search for the account again before retrying.',
        account_not_found: 'No account was found for this email address.',
        reason_required: 'An internal reason is required for a suspension or ban.',
        suspended_until_must_be_a_future_unix_timestamp: 'The suspension end must be in the future.',
        deleted_account_cannot_be_restored: 'A deleted account cannot be restored from this panel.',
        account_status_update_failed: 'The account status could not be saved.'
      }
      return messages[code] || fallback
    }
  }
}
</script>

<style scoped lang="scss">
.account-access-page {
  --admin-blue: #1159af;
  --admin-blue-soft: #edf4fc;
  --ink: #1f2d3d;
  --muted: #66758a;
  --line: #e2e8f0;
  max-width: 1080px;
  padding: 24px 26px 40px;
  color: var(--ink);
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2,
.section-heading h3,
.account-identity h3,
.empty-panel h3 {
  margin: 0;
  color: #172436;
}

.page-header h2 {
  font-size: 22px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.page-header p,
.section-heading p,
.empty-panel p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 13px;
}

.search-panel,
.account-panel,
.update-panel,
.empty-panel {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(31, 45, 61, 0.03);
}

.search-panel,
.update-panel {
  padding: 20px;
}

.section-heading,
.account-identity,
.search-form,
.status-option,
.update-actions {
  display: flex;
  align-items: center;
}

.section-heading {
  gap: 12px;
}

.section-step {
  display: inline-flex;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  background: var(--admin-blue);
  border-radius: 50%;
}

.section-heading h3,
.account-identity h3 {
  font-size: 17px;
  font-weight: 650;
}

.search-form {
  gap: 10px;
  max-width: 680px;
  margin-top: 18px;
}

.search-form ::v-deep .el-input {
  flex: 1;
}

.search-alert,
.effective-alert,
.impact-alert {
  margin-top: 16px;
}

.account-panel,
.update-panel,
.empty-panel {
  margin-top: 18px;
}

.account-panel {
  overflow: hidden;
}

.account-identity {
  padding: 20px;
  border-bottom: 1px solid var(--line);
}

.account-avatar {
  display: inline-flex;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  margin-right: 13px;
  color: #0d4d9a;
  font-size: 18px;
  font-weight: 700;
  background: var(--admin-blue-soft);
  border-radius: 50%;
}

.identity-label,
.status-summary span,
.account-details dt,
.field-label {
  color: #6b788a;
  font-size: 11px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.045em;
}

.user-id {
  display: block;
  margin-top: 3px;
  color: #8a96a6;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
}

.status-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 7px;
  margin-left: auto;
}

.effective-alert {
  margin-right: 20px;
  margin-left: 20px;
}

.account-details {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0;
  padding: 5px 20px 20px;
}

.account-details > div {
  min-width: 0;
  padding: 15px 16px 12px 0;
}

.account-details dd {
  margin: 7px 0 0;
  overflow-wrap: anywhere;
  color: #2f3c4d;
  font-size: 13px;
  line-height: 1.45;
}

.wide-detail {
  grid-column: span 3;
  border-top: 1px solid #edf0f4;
}

.update-heading {
  margin-bottom: 18px;
}

.status-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.status-option {
  position: relative;
  min-height: 78px;
  padding: 13px 38px 13px 13px;
  color: var(--ink);
  font: inherit;
  text-align: left;
  background: #ffffff;
  border: 1px solid #dfe5ec;
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.status-option:hover {
  border-color: #a8b5c6;
}

.status-option.selected {
  border-color: var(--admin-blue);
  box-shadow: 0 0 0 1px var(--admin-blue);
}

.status-active.selected {
  background: #f1fbf5;
  border-color: #3c9a62;
  box-shadow: 0 0 0 1px #3c9a62;
}

.status-suspended.selected {
  background: #fff9ed;
  border-color: #d79626;
  box-shadow: 0 0 0 1px #d79626;
}

.status-banned.selected {
  background: #fff3f3;
  border-color: #cf4a4e;
  box-shadow: 0 0 0 1px #cf4a4e;
}

.status-icon {
  display: inline-flex;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: #607086;
  background: #f0f3f7;
  border-radius: 50%;
}

.status-copy strong,
.status-copy small {
  display: block;
}

.status-copy strong {
  font-size: 14px;
}

.status-copy small {
  margin-top: 4px;
  color: #738196;
  font-size: 11px;
  line-height: 1.35;
}

.selected-check {
  position: absolute;
  top: 12px;
  right: 12px;
  color: var(--admin-blue);
  font-weight: 700;
}

.status-form {
  margin-top: 20px;
}

.form-block {
  margin-bottom: 20px;
  padding: 16px;
  background: #f7f9fc;
  border: 1px solid #e7ebf1;
  border-radius: 5px;
}

.field-label {
  margin-bottom: 10px;
}

.suspension-picker {
  width: 250px;
  margin-left: 10px;
}

.field-error,
.field-help {
  margin: 7px 0 0;
  font-size: 11px;
}

.field-error {
  color: #cf4a4e;
}

.field-help {
  color: #8591a1;
}

.label-info {
  margin-left: 5px;
  color: #8b97a7;
  cursor: help;
}

.optional-label {
  margin-left: 7px;
  color: #99a3b1;
  font-size: 11px;
  font-weight: 400;
}

.update-actions {
  justify-content: flex-end;
  gap: 8px;
  padding-top: 17px;
  border-top: 1px solid #edf0f4;
}

.no-change {
  margin-right: auto;
  color: #8a96a6;
  font-size: 12px;
}

.empty-panel {
  padding: 54px 20px;
  text-align: center;
}

.empty-icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: #6f7f93;
  font-size: 20px;
  background: #f0f3f7;
  border-radius: 50%;
}

.password-confirmation {
  margin: 8px 0 16px;
}

.email-dialog-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

::v-deep .el-dialog {
  max-width: calc(100vw - 32px);
  overflow-wrap: anywhere;
}

@media (max-width: 900px) {
  .status-options,
  .account-details {
    grid-template-columns: 1fr;
  }

  .wide-detail {
    grid-column: span 1;
  }

  .search-form {
    align-items: stretch;
    flex-direction: column;
  }

  .suspension-picker {
    display: block;
    width: 100%;
    margin: 10px 0 0;
  }
}
</style>
